"use client";

import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {createTextarea} from "@gluestack-ui/textarea";
import React from "react";
import {TextInput, View} from "react-native";

const SCOPE = "TEXTAREA";
const UITextarea = createTextarea({
    Root: withStyleContext(View, SCOPE),
    Input: TextInput
});

const textareaStyle = tva({
    base:
        "w-full " +
        "h-[100px] " +
        "border " +
        "border-blueGray-200 " +
        "border-solid " +
        "rounded-sm " +
        "data-[disabled=true]:opacity-40 " +
        "data-[disabled=true]:bg-blueGray-100 " +
        "data-[disabled=true]:border-blueGray-300",

    variants: {
        variant: {
            default:
                "data-[invalid=true]:border-error-600 " +
                "data-[invalid=true]:hover:border-error-600 " +
                "data-[focus=true]:web:ring-2 " +
                "data-[focus=true]:web:ring-blueGray-200/[.5] " +
                "data-[invalid=true]:web:ring-1 " +
                "data-[invalid=true]:web:ring-error-600 " +
                "data-[invalid=true]:data-[focus=true]:web:ring-2 " +
                "data-[invalid=true]:data-[focus=true]:web:ring-error-600 " +
                "data-[invalid=true]:data-[disabled=true]:web:ring-1 " +
                "data-[invalid=true]:data-[disabled=true]:web:ring-error-600 "
        }
    }
});

const textareaInputStyle = tva({
    base:
        "py-2 px-3 " +
        "web:outline-0 " +
        "web:outline-none " +
        "flex-1 " +
        "font-body " +
        "leading-sm " +
        "text-sm " +
        "font-normal " +
        "text-blueGray-800 " +
        "flex-1 " +
        "placeholder:text-blueGray-400 " +
        "web:cursor-text " +
        "web:data-[disabled=true]:cursor-not-allowed " +
        "align-text-top "
});

type ITextareaProps = React.ComponentProps<typeof UITextarea> & VariantProps<typeof textareaStyle>;

const Textarea = React.forwardRef<React.ElementRef<typeof UITextarea>, ITextareaProps>(
    ({className, variant = "default", ...props}, ref) => {
        return (
            <UITextarea
                ref={ref}
                {...props}
                className={textareaStyle({variant, class: className})}
            />
        );
    }
);

type ITextareaInputProps = React.ComponentProps<typeof UITextarea.Input> &
    VariantProps<typeof textareaInputStyle>;

const TextareaInput = React.forwardRef<
    React.ElementRef<typeof UITextarea.Input>,
    ITextareaInputProps
>(({className, ...props}, ref) => {
    return (
        <UITextarea.Input
            ref={ref}
            {...props}
            className={textareaInputStyle({
                class: className
            })}
        />
    );
});

Textarea.displayName = "Textarea";
TextareaInput.displayName = "TextareaInput";

export {Textarea, TextareaInput};
